/**
 * iter314 — Fusion de fiches clients en double.
 * On affiche les fiches cochées côte à côte avec leur poids réel (véhicules, OR,
 * factures) et on choisit celle à garder. Tout bascule dessus, les autres sont
 * supprimées — une copie est conservée côté serveur en cas d'erreur.
 */
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Merge, Loader2, AlertTriangle, Car, FileText, Receipt } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Compteur = ({ Icon, valeur, libelle }) => (
  <span className="inline-flex items-center gap-1 text-xs text-gray-600" title={libelle}>
    <Icon className="w-3.5 h-3.5 text-gray-400" /> {valeur}
  </span>
);

export default function ClientsFusionDialog({ open, onClose, ids = [], onDone }) {
  const [items, setItems] = useState([]);
  const [keepId, setKeepId] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    if (!open || ids.length === 0) return;
    setChargement(true);
    try {
      const { data } = await api.post("/clients/selection/apercu", { ids });
      const trouves = (data.items || []).filter((i) => !i.introuvable);
      setItems(trouves);
      // Par défaut : la fiche la plus « lourde » (celle qui a le plus d'historique)
      const poids = (i) => i.dependances.or * 10 + i.dependances.factures * 10 + i.dependances.vehicules;
      setKeepId([...trouves].sort((a, b) => poids(b) - poids(a))[0]?.id || null);
    } catch {
      toast.error("Impossible de charger les fiches sélectionnées");
    } finally {
      setChargement(false);
    }
  }, [open, ids]);

  useEffect(() => { charger(); }, [charger]);

  const fusionner = async () => {
    const merge_ids = items.map((i) => i.id).filter((id) => id !== keepId);
    if (!keepId || merge_ids.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/clients/fusionner", { keep_id: keepId, merge_ids });
      const r = data.references_basculees || {};
      toast.success(
        `${merge_ids.length} fiche${merge_ids.length > 1 ? "s" : ""} fusionnée${merge_ids.length > 1 ? "s" : ""}`,
        { description: `${r.vehicules || 0} véhicule(s), ${r.or || 0} OR, ${r.factures || 0} facture(s) et ${r.devis || 0} devis rattachés à la fiche conservée.` },
      );
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fusion impossible");
    } finally {
      setBusy(false);
    }
  };

  const garde = items.find((i) => i.id === keepId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="sm:max-w-2xl" data-testid="clients-fusion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="w-5 h-5 text-[#52a675]" /> Fusionner {items.length} fiches clients
          </DialogTitle>
          <DialogDescription>
            Choisissez la fiche à conserver. Véhicules, OR, devis, factures et rendez-vous des
            autres fiches lui seront rattachés, puis les doublons seront supprimés.
          </DialogDescription>
        </DialogHeader>

        {chargement ? (
          <div className="py-10 text-center text-gray-400 text-sm">Chargement des fiches…</div>
        ) : (
          <div className="space-y-3">
            <div className="max-h-80 overflow-y-auto space-y-2" data-testid="fusion-liste">
              {items.map((i) => (
                <label
                  key={i.id}
                  className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    keepId === i.id
                      ? "border-[#52a675] bg-[#f0f9f4] ring-1 ring-[#52a675]"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                  data-testid={`fusion-option-${i.id}`}
                >
                  <input
                    type="radio"
                    name="client-a-garder"
                    className="mt-1 accent-[#52a675]"
                    checked={keepId === i.id}
                    onChange={() => setKeepId(i.id)}
                    data-testid={`fusion-radio-${i.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{i.label}</span>
                      {keepId === i.id && (
                        <Badge className="bg-[#52a675] hover:bg-[#52a675] text-white">À conserver</Badge>
                      )}
                      {i.archive && <Badge variant="outline" className="text-gray-500">Archivée</Badge>}
                      {i.num_bexio && <span className="text-xs text-gray-400">#{i.num_bexio}</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {[i.tel_principal, i.email, [i.npa, i.localite].filter(Boolean).join(" ")]
                        .filter(Boolean)
                        .join(" · ") || "Aucune coordonnée"}
                    </div>
                    <div className="flex gap-3 mt-1.5">
                      <Compteur Icon={Car} valeur={i.dependances.vehicules} libelle="véhicules" />
                      <Compteur Icon={FileText} valeur={i.dependances.or} libelle="ordres de réparation" />
                      <Compteur Icon={Receipt} valeur={i.dependances.factures} libelle="factures" />
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Les coordonnées conservées seront celles de <strong>{garde?.label || "la fiche choisie"}</strong>.
                Les autres fiches disparaîtront de la liste (une copie reste côté serveur).
              </span>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy} data-testid="fusion-annuler">
                Annuler
              </Button>
              <Button
                className="flex-1 bg-[#52a675] hover:bg-[#3d8a5c]"
                onClick={fusionner}
                disabled={busy || !keepId || items.length < 2}
                data-testid="fusion-confirmer"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
                Fusionner sur cette fiche
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
